import { Button } from '@base-ui/react/button';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { CONDITION_LABELS } from '@/admin/sitecontent/conditions';
import { OnboardingSequenceForm } from '@/admin/sitecontent/OnboardingSequenceForm';
import { HeaderRow, StatusMessage } from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { OnboardingHeader } from '@/admin/ui/ContentNav';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';

export const Route = createFileRoute('/admin/onboardings/$onboardingId')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: validateEditSearch,
  component: OnboardingDetail,
});

function OnboardingDetail() {
  const { t } = useTranslation();
  const { onboardingId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);
  const { data, isLoading, error } = api.getOnboardingSequence.useQuery({
    path: { onboarding_id: Number(onboardingId) },
  });

  if (isLoading)
    return <StatusMessage>{t('admin.onboardings.loadingOne')}</StatusMessage>;
  if (error)
    return (
      <StatusMessage $error>
        {t('admin.onboardings.errorLoadingOne')}
      </StatusMessage>
    );
  if (!data)
    return (
      <StatusMessage $error>{t('admin.onboardings.notFound')}</StatusMessage>
    );

  return (
    <DetailPage>
      <OnboardingHeader onboardingId={data.id} />
      {editing ? (
        <OnboardingSequenceForm
          key={data.id}
          sequence={data}
          onSaved={stopEditing}
          onCancel={stopEditing}
        />
      ) : (
        <>
          <HeaderRow>
            <SectionTitle>
              {data.title || t('admin.common.details')}
            </SectionTitle>
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
          </HeaderRow>
          <Section>
            <SectionTitle>{t('admin.common.details')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.title')}</FieldLabel>
                <FieldValue>{data.title || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[data.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  {t('admin.onboardings.col.activationCondition')}
                </FieldLabel>
                <FieldValue>
                  {data.activation_condition
                    ? t(CONDITION_LABELS[data.activation_condition])
                    : '—'}
                </FieldValue>
              </Field>
              {data.course_id !== null && (
                <Field>
                  <FieldLabel>{t('admin.onboardings.course')}</FieldLabel>
                  <FieldValue>
                    {data.course_title
                      ? `${data.course_title} (#${data.course_id})`
                      : t('admin.onboardings.courseNotFound', {
                          id: data.course_id,
                          interpolation: { escapeValue: false },
                        })}
                  </FieldValue>
                </Field>
              )}
              <Field>
                <FieldLabel>{t('admin.onboardings.col.published')}</FieldLabel>
                <FieldValue>
                  <Badge $variant={data.is_published ? 'success' : 'neutral'}>
                    {data.is_published
                      ? t('admin.common.yes')
                      : t('admin.common.no')}
                  </Badge>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardings.col.menuOrder')}</FieldLabel>
                <FieldValue>{data.menu_order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardings.col.slides')}</FieldLabel>
                <FieldValue>{data.slides.length}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardings.pending')}</FieldLabel>
                <FieldValue>{data.pending_count}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardings.completed')}</FieldLabel>
                <FieldValue>{data.completed_count}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>
        </>
      )}
    </DetailPage>
  );
}
